<script lang="ts" setup>
import DropFile from '@legacy/components/common/form/input/DropFile.vue';
import ApiError from '@legacy/js/model/Error';
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import SphCheckbox from '@/components/base/checkbox/';
import { SphFieldset, SphFormField } from '@/components/base/form-field';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import { Separator as SphSeparator } from '@/components/base/separator';
import SphSpinner from '@/components/base/spinner/';
import { SphTextarea } from '@/components/base/textarea';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { useDialog } from '@/plugins/dialog';

interface Kindergarten {
    id: number
    name: string
}

interface EmailOptionsResponse {
    replyTos: string[]
}

defineOptions({
    compatConfig: { MODE: 3 },
});
const props = defineProps({
    federationId: {
        type: Number,
        required: true,
    },
    allKindergartens: {
        type: Array<Kindergarten>,
        required: true,
    },
});
const emit = defineEmits<{
    cancel: []
    done: []
}>();
const selectedKindergartenIds = defineModel<number[]>('selectedIds', { required: true });
const { i18n } = useI18n();
const { notify } = useNotifications();
const { dialog } = useDialog();

const loading = ref<boolean>(false);
const sending = ref<boolean>(false);
const filesProcessing = ref<boolean>(false);
const apiError = ref<InstanceType<typeof ApiError> | null>(null);
const hasFileError = ref<boolean>(false);

const kindergartenOptions = computed(() =>
    props.allKindergartens
        .map((k) => ({ value: k.id, label: k.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' })),
);

// Recipient filters
const includesExecutives = ref<boolean>(false);
const recipientEmployeeRoles = ref<{
    admin: boolean
    subadmin: boolean
    employee: boolean
}>({
    admin: false,
    subadmin: false,
    employee: false,
});

const includesParentUsers = ref<boolean>(false);
const primaryContactsOnly = ref<boolean>(false);
const parentRepresentativesOnly = ref<boolean>(false);
const includesFederationUsers = ref<boolean>(false);
const mailCopy = ref<boolean>(false);

// When parent checkbox is unchecked, uncheck sub-checkboxes
watch(includesParentUsers, (newValue) => {
    if (!newValue) {
        primaryContactsOnly.value = false;
        parentRepresentativesOnly.value = false;
    }
});

// When sub-checkbox is checked, check parent checkbox
watch(primaryContactsOnly, (newValue) => {
    if (newValue) {
        includesParentUsers.value = true;
    }
});

watch(parentRepresentativesOnly, (newValue) => {
    if (newValue) {
        includesParentUsers.value = true;
    }
});

const emailOptions = ref<{
    replyTos: Array<{ value: string, label: string }>
}>({
    replyTos: [],
});

const email = ref<{
    replyTo: string | undefined
    subject: string | undefined
    body: string | undefined
    files: Array<{ type: string, name: string, base64: string }>
}>({ replyTo: undefined, subject: undefined, body: undefined, files: [] });

const hasAnyRecipientSelected = computed(() => {
    return recipientEmployeeRoles.value.admin
        || recipientEmployeeRoles.value.subadmin
        || recipientEmployeeRoles.value.employee
        || includesParentUsers.value
        || includesFederationUsers.value
        || includesExecutives.value
        || mailCopy.value;
});

const SUBJECT_MAX_LENGTH = 255;
const BODY_MAX_LENGTH = 10000;
const subjectAtLimit = computed(() => (email.value.subject?.length ?? 0) >= SUBJECT_MAX_LENGTH);
const bodyAtLimit = computed(() => (email.value.body?.length ?? 0) >= BODY_MAX_LENGTH);

function buildRequestData() {
    const roles: string[] = [];
    if (recipientEmployeeRoles.value.admin) {
        roles.push('ROLE_ADMIN');
    }
    if (recipientEmployeeRoles.value.subadmin) {
        roles.push('ROLE_SUBADMIN');
    }
    if (recipientEmployeeRoles.value.employee) {
        roles.push('ROLE_EMPLOYEE');
    }

    return {
        kindergartenIds: selectedKindergartenIds.value,
        includesExecutives: includesExecutives.value,
        recipientEmployeeRoles: roles,
        includesParentUsers: includesParentUsers.value,
        primaryContactsOnly: primaryContactsOnly.value,
        parentRepresentativesOnly: parentRepresentativesOnly.value,
        includesFederationUsers: includesFederationUsers.value,
        mailCopy: mailCopy.value,
        replyTo: email.value.replyTo,
        subject: email.value.subject,
        body: email.value.body,
        files: email.value.files,
    };
}

async function sendEmails() {
    if (sending.value) {
        return;
    }

    apiError.value = null;

    if (!hasAnyRecipientSelected.value) {
        notify.error(i18n.t('Bitte wählen Sie mindestens eine Empfängergruppe aus.'));
        return;
    }

    const data = buildRequestData();
    const { files: _files, ...countData } = data;

    sending.value = true;
    try {
        const countResponse = await axios.post(`/federations/${props.federationId}/email/kindergarten/count`, countData);
        const { recipientsCount, kindergartenCount } = countResponse.data;

        if (recipientsCount === 0) {
            notify.error(i18n.t('Mit der gegebenen Auswahl konnten keine Empfänger ermittelt werden.'));
            return;
        }

        const confirmed = await dialog.confirm({
            title: i18n.t('E-Mail senden'),
            message: i18n.t('Die E-Mail wird an {recipientsCount} Empfänger in {kindergartenCount} Einrichtungen gesendet. Fortfahren?', {
                recipientsCount,
                kindergartenCount,
            }),
            confirmButtonLabel: i18n.t('Senden'),
        });

        if (!confirmed) {
            return;
        }

        await axios.post(`/federations/${props.federationId}/email/kindergarten`, data);
        emit('done');
    } catch (err: any) {
        apiError.value = ApiError.fromResponse(err.response);
        notify.error(err.response?.data?.error?.message ?? i18n.t('Ein Fehler ist aufgetreten.'));
    } finally {
        sending.value = false;
    }
}

function cancel() {
    emit('cancel');
}

function handleFiles(files: Array<File & { base64?: string }>) {
    email.value.files = files.map((file) => ({
        type: file.type,
        name: file.name,
        base64: file.base64 ?? '',
    }));
}

onMounted(() => {
    loading.value = true;
    axios.get<{ data: EmailOptionsResponse }>(`/federations/${props.federationId}/email/kindergarten`)
        .then((response) => {
            const data = response.data.data;
            emailOptions.value.replyTos = data.replyTos.map((e) => ({ value: e, label: e }));
            if (emailOptions.value.replyTos.length === 1) {
                email.value.replyTo = emailOptions.value.replyTos[0].value;
            }
        })
        .catch((error) => {
            console.error('Error fetching options:', error);
        })
        .finally(() => {
            loading.value = false;
        })
    ;
});
</script>

<template>
    <SphSpinner
        v-if="loading"
        class="min-h-32"
        variant="centered"
    />
    <div v-else>
        <SphFormField
            :label="i18n.t('Ausgewählte Einrichtungen')"
            class="mb-4"
        >
            <SphSelect
                v-model="selectedKindergartenIds"
                :options="kindergartenOptions"
                :placeholder="i18n.t('Einrichtungen auswählen')"
                :multiple="{ noTags: true }"
                searchable
            />
        </SphFormField>
        <div class="kgr-postfix kgr-postfix--between kgr-postfix--top kgr-row">
            <div class="kgr-postfix__fixed">
                <div class="mb-4">
                    <div class="kgr-text kgr-text--bold mb-2">
                        {{ i18n.t('Einrichtungs-Mitarbeiter') }}
                    </div>
                    <SphFieldset variant="label">
                        <SphFormField
                            :label="i18n.t('Einrichtungs-Leitungen')"
                            layout="choice"
                        >
                            <SphCheckbox v-model="includesExecutives" />
                        </SphFormField>
                        <SphFormField
                            :label="i18n.t('Administratoren')"
                            layout="choice"
                        >
                            <SphCheckbox v-model="recipientEmployeeRoles.admin" />
                        </SphFormField>
                        <SphFormField
                            :label="i18n.t('Sub-Administratoren')"
                            layout="choice"
                        >
                            <SphCheckbox v-model="recipientEmployeeRoles.subadmin" />
                        </SphFormField>
                        <SphFormField
                            :label="i18n.t('Mitarbeiter')"
                            layout="choice"
                        >
                            <SphCheckbox v-model="recipientEmployeeRoles.employee" />
                        </SphFormField>
                    </SphFieldset>
                </div>

                <div class="mb-4">
                    <div class="kgr-text kgr-text--bold mb-2">
                        {{ i18n.t('Bezugspersonen') }}
                    </div>
                    <SphFieldset variant="label">
                        <SphFormField
                            :label="i18n.t('Bezugspersonen')"
                            layout="choice"
                        >
                            <SphCheckbox v-model="includesParentUsers" />
                        </SphFormField>
                        <div class="ml-6">
                            <div style="margin-bottom: 4px;">
                                <SphFormField
                                    :label="i18n.t('Nur primäre Kontaktpersonen')"
                                    layout="choice"
                                >
                                    <SphCheckbox v-model="primaryContactsOnly" />
                                </SphFormField>
                            </div>
                            <div>
                                <SphFormField
                                    :label="i18n.t('Nur Elternvertreter')"
                                    layout="choice"
                                >
                                    <SphCheckbox v-model="parentRepresentativesOnly" />
                                </SphFormField>
                            </div>
                        </div>
                    </SphFieldset>
                </div>

                <div class="mb-4">
                    <div class="kgr-text kgr-text--bold mb-2">
                        {{ i18n.t('Träger') }}
                    </div>
                    <SphFormField
                        :label="i18n.t('Trägermitarbeiter')"
                        layout="choice"
                    >
                        <SphCheckbox v-model="includesFederationUsers" />
                    </SphFormField>
                </div>

                <SphSeparator class="mb-4" orientation="horizontal" />

                <div class="mb-4">
                    <SphFormField
                        :label="i18n.t('Kopie an mich')"
                        layout="choice"
                    >
                        <SphCheckbox v-model="mailCopy" />
                    </SphFormField>
                </div>
            </div>
            <div class="kgr-postfix__fixed space-y-4 w-full ml-12">
                <SphFormField
                    :errors="apiError?.messagesForField('replyTo', true)"
                    :label="i18n.t('Antwortadresse*')"
                >
                    <SphSelect
                        v-model="email.replyTo"
                        :options="emailOptions.replyTos"
                    />
                </SphFormField>
                <div>
                    <SphFormField
                        :errors="apiError?.messagesForField('subject', true)"
                        :label="i18n.t('Betreff*')"
                    >
                        <SphInput
                            v-model="email.subject"
                            :maxlength="SUBJECT_MAX_LENGTH"
                        />
                    </SphFormField>
                    <p
                        v-if="subjectAtLimit"
                        class="text-sm text-neutral-600 mt-1"
                    >
                        {{ i18n.t('Maximal {count} Zeichen', { count: SUBJECT_MAX_LENGTH }) }}
                    </p>
                </div>
                <div>
                    <SphFormField
                        :errors="apiError?.messagesForField('body', true)"
                        :label="i18n.t('Nachricht*')"
                    >
                        <SphTextarea
                            v-model="email.body"
                            :maxlength="BODY_MAX_LENGTH"
                            class="h-92"
                            resizable
                        />
                    </SphFormField>
                    <p
                        v-if="bodyAtLimit"
                        class="text-sm text-neutral-600 mt-1"
                    >
                        {{ i18n.t('Maximal {count} Zeichen', { count: BODY_MAX_LENGTH.toLocaleString('de-DE') }) }}
                    </p>
                </div>
                <DropFile
                    :max-total-size="20"
                    emit-base64-files
                    multiple
                    show-size
                    @error="hasFileError = true"
                    @files="handleFiles"
                    @error-clear="hasFileError = false"
                    @update:processing="filesProcessing = $event"
                />
                <p class="text-sm text-neutral-600 mt-1">
                    {{ i18n.t('Alle Anhänge zusammen dürfen höchstens 20 MB groß sein.') }}
                </p>
            </div>
        </div>
        <br />
        <div class="kgr-postfix kgr-postfix--between">
            <div class="kgr-postfix__fixed">
                <SphButton @click="cancel">
                    {{ i18n.t('Abbrechen') }}
                </SphButton>
            </div>
            <div class="kgr-postfix__fixed">
                <SphButton
                    :disabled="sending || filesProcessing || hasFileError || !email.replyTo || !email.subject || !email.body || !hasAnyRecipientSelected || selectedKindergartenIds.length === 0"
                    :loading="sending"
                    variant="primary"
                    @click="sendEmails"
                >
                    {{ i18n.t('E-Mail senden') }}
                </SphButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>

</style>
